import { logEvent } from '../debug/debugLog';

// The playlist on the front page
export const PLAYLIST_ID = 'PLdb-gmLYl9AYN0hIWzLDRLomYJ9wMl6t1';
export const PLAYLIST_URL = `https://www.youtube.com/playlist?list=${PLAYLIST_ID}`;

const API_SRC = 'https://www.youtube.com/iframe_api';
const API_TIMEOUT_MS = 15000;

// YT.PlayerState values
export const PLAYER_STATE = { ENDED: 0, PLAYING: 1, PAUSED: 2, BUFFERING: 3, CUED: 5 };

// What YouTube's player error codes mean, in words a visitor can follow
export function describeError(code) {
  switch (code) {
    case 2: return 'the playlist link looks invalid';
    case 100: return 'a video was removed or is private';
    case 101:
    case 150: return "a video's owner doesn't allow it to be embedded";
    case 152:
    case 153: return "YouTube didn't accept this site as an embed";
    default: return 'the player hit a problem';
  }
}

let apiPromise = null;

// Loads YouTube's IFrame Player API once and resolves with window.YT
export function loadYouTubeApi() {
  if (window.YT && window.YT.Player) {
    return Promise.resolve(window.YT);
  }
  if (!apiPromise) {
    const startedAt = performance.now();
    logEvent('yt-api', 'script-requested', { src: API_SRC });
    apiPromise = new Promise((resolve, reject) => {
      let finished = false;
      let timeout = null;
      const fail = (message) => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        logEvent('yt-api', 'script-failed', { message, afterMs: Math.round(performance.now() - startedAt) });
        apiPromise = null;
        reject(new Error(message));
      };
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout);
        logEvent('yt-api', 'script-ready', { afterMs: Math.round(performance.now() - startedAt) });
        if (previous) previous();
        resolve(window.YT);
      };
      const script = document.createElement('script');
      script.src = API_SRC;
      script.async = true;
      script.onerror = () => fail('The YouTube player could not be loaded');
      document.head.appendChild(script);
      timeout = setTimeout(() => fail('Timed out loading the YouTube player'), API_TIMEOUT_MS);
    });
  }
  return apiPromise;
}

const attempt = (fn) => {
  try {
    return fn();
  } catch (error) {
    return undefined;
  }
};

// Play a list of video ids we already know (rather than asking YouTube to load
// a playlist, which it only does once the first video has loaded). Because we
// hold the list, "next" always works and a bad video cannot trap the player.
export function startCuratedPlayback(player, ids) {
  attempt(() => player.loadPlaylist({ playlist: ids, index: 0 }));
  attempt(() => player.setLoop(true));
}

// ---- track details (shown even when a video cannot be played) ----

const VIDEO_ID = /^[\w-]{11}$/;
const OEMBED_TIMEOUT_MS = 8000;

export const isVideoId = (id) => typeof id === 'string' && VIDEO_ID.test(id);
export const watchUrl = (id) => `https://www.youtube.com/watch?v=${id}`;
export const thumbnailUrl = (id) => `https://i.ytimg.com/vi/${id}/mqdefault.jpg`;

// What the player knows about the current and the next track. Works with the
// shuffled order, because getPlaylist() reflects it once shuffle is on.
export function readTrack(player) {
  const list = attempt(() => player.getPlaylist()) || [];
  const rawIndex = attempt(() => player.getPlaylistIndex());
  const index = Number.isInteger(rawIndex) && rawIndex >= 0 ? rawIndex : null;
  const data = attempt(() => player.getVideoData()) || {};
  const id = isVideoId(data.video_id) ? data.video_id : (index !== null && isVideoId(list[index]) ? list[index] : null);
  const nextId = index !== null && list.length > 1 && isVideoId(list[(index + 1) % list.length])
    ? list[(index + 1) % list.length]
    : null;
  const duration = Number(attempt(() => player.getDuration())) || 0;

  return {
    id,
    nextId,
    index,
    total: list.length || null,
    title: typeof data.title === 'string' ? data.title : '',
    author: typeof data.author === 'string' ? data.author : '',
    duration
  };
}

// Ask YouTube's oEmbed service about a video. Its HTTP status says a lot about
// why a video will not play (e.g. 401 usually means embedding is switched off).
// Resolves with { state: 'ok' | 'refused' | 'unavailable', httpStatus?, title?, author? }
export async function fetchVideoInfo(id, { fetchFn = (...args) => fetch(...args) } = {}) {
  if (!isVideoId(id)) return { state: 'unavailable' };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), OEMBED_TIMEOUT_MS);
  try {
    const url = `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(watchUrl(id))}`;
    const response = await fetchFn(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
    if (!response.ok) {
      return { state: 'refused', httpStatus: response.status };
    }
    const data = await response.json();
    return {
      state: 'ok',
      httpStatus: response.status,
      title: typeof data.title === 'string' ? data.title : '',
      author: typeof data.author_name === 'string' ? data.author_name : ''
    };
  } catch (error) {
    // offline, blocked, or the browser refused the cross-site request
    return { state: 'unavailable' };
  } finally {
    clearTimeout(timer);
  }
}

export function describeEmbedCheck(info) {
  if (!info) return 'checking...';
  switch (info.state) {
    case 'ok': return 'the owner allows embedding';
    case 'refused':
      if (info.httpStatus === 401) return 'embedding looks disabled by the owner (HTTP 401)';
      if (info.httpStatus === 403) return 'restricted or private (HTTP 403)';
      if (info.httpStatus === 404) return 'not found, removed or private (HTTP 404)';
      return `YouTube answered HTTP ${info.httpStatus}`;
    default: return "couldn't be checked from this browser";
  }
}

export function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}
